import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Layout } from './components/layout/Layout'
import { useEffect, lazy, Suspense } from 'react'
import { GA_ID } from './lib/gtag'

// New template pages (from DB)
const WatermarkPage = lazy(() => import('./pages/watermarks/WatermarkPage'))
const WatermarksHub = lazy(() => import('./pages/watermarks/WatermarksHub'))
const ServicePage = lazy(() => import('./pages/services/ServicePage'))
const ServicesHub = lazy(() => import('./pages/services/ServicesHub'))
// Platforms - temporarily disabled
// const PlatformPage = lazy(() => import('./pages/platforms/PlatformPage'))
// const PlatformsHub = lazy(() => import('./pages/platforms/PlatformsHub'))

// Detectors
const DetectorSubPage = lazy(() => import('./pages/detector/DetectorSubPage').then(m => ({ default: m.DetectorSubPage })))
const NewDetectorPage = lazy(() => import('./pages/detectors/DetectorPage'))

// Models
const NewModelPage = lazy(() => import('./pages/models/ModelPage'))
const ModelsHub = lazy(() => import('./pages/models/ModelsHub'))

// Tools
const Undetectable = lazy(() => import('./pages/tools/Undetectable').then(m => ({ default: m.Undetectable })))
const EnhanceRealism = lazy(() => import('./pages/tools/EnhanceRealism').then(m => ({ default: m.EnhanceRealism })))
const AddNoise = lazy(() => import('./pages/tools/AddNoise').then(m => ({ default: m.AddNoise })))
const MetadataCleaner = lazy(() => import('./pages/tools/MetadataCleaner').then(m => ({ default: m.MetadataCleaner })))

// Blog
const BlogHub = lazy(() => import('./pages/blog/BlogHub').then(m => ({ default: m.BlogHub })))
const BlogPost = lazy(() => import('./pages/blog/BlogPost').then(m => ({ default: m.BlogPost })))

// Arena
const Arena = lazy(() => import('./pages/Arena'))

// Check (public multi-detector image check)
const Check = lazy(() => import('./pages/Check'))

// Benchmark - now the homepage
const BenchmarkPage = lazy(() => import('./pages/benchmark/BenchmarkPage'))
const DetectorPage = lazy(() => import('./pages/benchmark/DetectorPage'))
const ModelPage = lazy(() => import('./pages/benchmark/ModelPage'))

// Static Pages
const Pricing = lazy(() => import('./pages/Pricing').then(m => ({ default: m.Pricing })))
const API = lazy(() => import('./pages/API').then(m => ({ default: m.API })))
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })))
const Contact = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })))
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })))
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })))
const Partners = lazy(() => import('./pages/Partners').then(m => ({ default: m.Partners })))
const Dataset = lazy(() => import('./pages/Dataset').then(m => ({ default: m.Dataset })))
const Methodology = lazy(() => import('./pages/Methodology').then(m => ({ default: m.Methodology })))
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })))
const DatasetRedirect = lazy(() => import('./pages/DatasetRedirect').then(m => ({ default: m.DatasetRedirect })))

// Loading fallback
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
    </div>
  )
}

export function App() {
  const location = useLocation()

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', GA_ID, { page_path: location.pathname })
    }
  }, [location.pathname])

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        {/* Homepage - Benchmark */}
        <Route index element={
          <Suspense fallback={<PageLoader />}>
            <BenchmarkPage />
          </Suspense>
        } />

        {/* Remove Tools - REMOVED: old static pages, use /watermarks/:slug instead */}

        {/* Detector - redirect to /check */}
        <Route path="detector" element={<Navigate to="/check" replace />} />
        <Route path="detector/:type" element={
          <Suspense fallback={<PageLoader />}>
            <DetectorSubPage />
          </Suspense>
        } />

        {/* Bypass - REMOVED: old static pages, use /platforms/:slug instead */}

        {/* Tools */}
        <Route path="undetectable" element={
          <Suspense fallback={<PageLoader />}>
            <Undetectable />
          </Suspense>
        } />
        <Route path="enhance-realism" element={
          <Suspense fallback={<PageLoader />}>
            <EnhanceRealism />
          </Suspense>
        } />
        <Route path="add-noise" element={
          <Suspense fallback={<PageLoader />}>
            <AddNoise />
          </Suspense>
        } />
        <Route path="tools/metadata-cleaner" element={
          <Suspense fallback={<PageLoader />}>
            <MetadataCleaner />
          </Suspense>
        } />

        {/* Blog */}
        <Route path="blog" element={
          <Suspense fallback={<PageLoader />}>
            <BlogHub />
          </Suspense>
        } />
        <Route path="blog/:slug" element={
          <Suspense fallback={<PageLoader />}>
            <BlogPost />
          </Suspense>
        } />

        {/* Arena */}
        <Route path="arena" element={
          <Suspense fallback={<PageLoader />}>
            <Arena />
          </Suspense>
        } />
        {/* Leaderboard - redirect to homepage (now combined with benchmark) */}
        <Route path="leaderboard" element={<Navigate to="/" replace />} />

        {/* Check - public multi-detector image check */}
        <Route path="check" element={
          <Suspense fallback={<PageLoader />}>
            <Check />
          </Suspense>
        } />
        <Route path="check/:id" element={
          <Suspense fallback={<PageLoader />}>
            <Check />
          </Suspense>
        } />

        {/* Benchmark - redirect to homepage */}
        <Route path="benchmark" element={<Navigate to="/" replace />} />
        {/* Legacy benchmark routes - redirect to new URLs */}
        <Route path="benchmark/detector/:slug" element={
          <Suspense fallback={<PageLoader />}>
            <DetectorPage />
          </Suspense>
        } />
        <Route path="benchmark/model/:slug" element={
          <Suspense fallback={<PageLoader />}>
            <ModelPage />
          </Suspense>
        } />

        {/* NEW: Detectors (template pages from DB) */}
        <Route path="detectors/:slug" element={
          <Suspense fallback={<PageLoader />}>
            <NewDetectorPage />
          </Suspense>
        } />

        {/* NEW: Models (template pages from DB) */}
        <Route path="models" element={
          <Suspense fallback={<PageLoader />}>
            <ModelsHub />
          </Suspense>
        } />
        <Route path="models/:slug" element={
          <Suspense fallback={<PageLoader />}>
            <NewModelPage />
          </Suspense>
        } />

        {/* NEW: Services (template pages from DB) */}
        <Route path="services" element={
          <Suspense fallback={<PageLoader />}>
            <ServicesHub />
          </Suspense>
        } />
        <Route path="services/:slug" element={
          <Suspense fallback={<PageLoader />}>
            <ServicePage />
          </Suspense>
        } />

        {/* NEW: Watermarks - enhanced remove pages from DB */}
        <Route path="watermarks" element={
          <Suspense fallback={<PageLoader />}>
            <WatermarksHub />
          </Suspense>
        } />
        <Route path="watermarks/:slug" element={
          <Suspense fallback={<PageLoader />}>
            <WatermarkPage />
          </Suspense>
        } />

        {/* Platforms - temporarily disabled */}
        {/* <Route path="platforms" element={
          <Suspense fallback={<PageLoader />}>
            <PlatformsHub />
          </Suspense>
        } />
        <Route path="platforms/:slug" element={
          <Suspense fallback={<PageLoader />}>
            <PlatformPage />
          </Suspense>
        } /> */}

        {/* Static Pages */}
        <Route path="pricing" element={
          <Suspense fallback={<PageLoader />}>
            <Pricing />
          </Suspense>
        } />
        <Route path="api" element={
          <Suspense fallback={<PageLoader />}>
            <API />
          </Suspense>
        } />
        <Route path="about" element={
          <Suspense fallback={<PageLoader />}>
            <About />
          </Suspense>
        } />
        <Route path="contact" element={
          <Suspense fallback={<PageLoader />}>
            <Contact />
          </Suspense>
        } />
        <Route path="terms" element={
          <Suspense fallback={<PageLoader />}>
            <Terms />
          </Suspense>
        } />
        <Route path="privacy" element={
          <Suspense fallback={<PageLoader />}>
            <Privacy />
          </Suspense>
        } />
        <Route path="partners" element={
          <Suspense fallback={<PageLoader />}>
            <Partners />
          </Suspense>
        } />
        <Route path="benchmark-dataset" element={
          <Suspense fallback={<PageLoader />}>
            <Dataset />
          </Suspense>
        } />
        <Route path="methodology" element={
          <Suspense fallback={<PageLoader />}>
            <Methodology />
          </Suspense>
        } />

        {/* Dataset Downloads - canonical URLs */}
        <Route path="datasets/:version" element={
          <Suspense fallback={<PageLoader />}>
            <DatasetRedirect />
          </Suspense>
        } />

        {/* 404 */}
        <Route path="*" element={
          <Suspense fallback={<PageLoader />}>
            <NotFound />
          </Suspense>
        } />
      </Route>
    </Routes>
  )
}
